/**
 * `kind: 'verdict'`. `subject` is a participant id, named here. Neither it nor `prompt`
 * may hold the answer: a host-targeted request reaches a driving TV.
 * Y/N are bound in the card so the keys exist exactly when the control does.
 */

import { useEffect } from 'react';
import type { DecisionSpec } from '@gsf/contract';
import type { RendererProps } from '../renderer';

type Spec = Extract<DecisionSpec, { kind: 'verdict' }>;

/** A hotkey must not fire while somebody is typing a reply into a text box. */
function isTyping(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  return element !== null && /^(INPUT|TEXTAREA|SELECT)$/.test(element.tagName);
}

export function VerdictCard({ request, spec, submit, nameOf, disabled, size }: RendererProps<Spec>) {
  const who = nameOf(spec.subject) ?? spec.subject;

  useEffect(() => {
    if (disabled) return;
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === 'y') submit('correct');
      else if (key === 'n') submit('incorrect');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, submit]);

  // Blur so the focused button doesn't eat the next Space (which reveals the next clue).
  const rule = (verdict: 'correct' | 'incorrect') => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    submit(verdict);
  };

  // A fragment: on a TV these sit in the caller's row beside the game's flow buttons.
  if (size === 'lg') {
    return (
      <>
        <button
          className="btn btn-good tv-action"
          type="button"
          disabled={disabled}
          title={`Rule ${who} correct`}
          onClick={rule('correct')}
        >
          {who} correct (Y)
        </button>
        <button
          className="btn btn-bad tv-action"
          type="button"
          disabled={disabled}
          title={`Rule ${who} wrong`}
          onClick={rule('incorrect')}
        >
          {who} wrong (N)
        </button>
      </>
    );
  }

  return (
    <div className="judge-strip">
      <span className="judge-who">{who}</span>
      <span className="muted">{request.prompt}</span>
      <span className="spacer" />
      <button className="btn btn-good" type="button" disabled={disabled} onClick={rule('correct')}>
        Correct (Y)
      </button>
      <button className="btn btn-bad" type="button" disabled={disabled} onClick={rule('incorrect')}>
        Incorrect (N)
      </button>
    </div>
  );
}
